import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Select } from "antd";
import { FilterOutlined, PlusOutlined } from "@ant-design/icons";
import FormItem from "@/common/FormItem/FormItem";
import PageTitle from "@/common/PageTitle/PageTitle";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import {
  useCreateInstallerVendorMutation,
  useCreateMusicVendorMutation,
  useListMasterAccountsQuery,
  useStartImpersonationMutation,
  useReserveSupportTicketMutation,
  type CreateMasterAccountRequest,
  type MasterAccountRow,
} from "@/features/accounts/accountsApi";

export type VendorKind = "music-vendor" | "installer-vendor";

interface VendorAccountsPageProps {
  kind: VendorKind;
  title: string;
  idColumnLabel: string;
  addButtonLabel: string;
  addModalTitle: string;
  styles: { [key: string]: string };
  /** Optional extra action buttons rendered after Impersonate. */
  renderExtraActions?: (vendor: VendorListRow) => React.ReactNode;
}

export interface VendorListRow {
  vendorId: string;
  masterAccountId: string;
  companyName: string;
  companyAddress: string;
  companyNumber: string;
  ownerEmail: string | null;
  ownerDisplayName: string | null;
  ownerAuth0Sub: string | null;
  status: string;
  createdOn: string;
  pinLabel: string;
  city: string;
  state: string;
  region: string;
  kind: string;
}

const emptyForm: CreateMasterAccountRequest = {
  businessName: "",
  address_street: "",
  address_city: "",
  address_state: "",
  address_zipCode: "",
  address_country: "US",
  billingPhone: "",
  billingEmail: "",
  ownerFirstName: "",
  ownerLastName: "",
  ownerEmail: "",
  securityPin: "",
};

function getMaUiOrigin(): string {
  return (
    process.env.REACT_APP_MA_UI_ORIGIN ||
    (process.env.REACT_APP_ENV === "production"
      ? "https://myaccount.krosskast.com"
      : "https://staging.myaccount.krosskast.com")
  ).replace(/\/$/, "");
}

function mapStatusForBadge(status: string): string {
  if (status === "brand-review-pending") return "pending_approval";
  return status || "active";
}

function formatStatusLabel(status: string): string {
  return mapStatusForBadge(status)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

function toVendorRow(row: MasterAccountRow): VendorListRow {
  return {
    vendorId: row.masterAccountId,
    masterAccountId: row.masterAccountId,
    companyName: row.businessName || row.companyName || "—",
    companyAddress: row.companyAddress || "—",
    companyNumber: row.companyNumber || row.billingPhone || "—",
    ownerEmail: row.ownerEmail,
    ownerDisplayName: row.ownerDisplayName,
    ownerAuth0Sub: row.ownerAuth0Sub,
    status: mapStatusForBadge(row.status),
    createdOn: row.createdOn || "—",
    pinLabel: row.pinStatus === "set" ? "Set" : "Not Set",
    city: row.address_city || "",
    state: row.address_state || "",
    region: "",
    kind: row.kind || "",
  };
}

const VendorAccountsPage: React.FC<VendorAccountsPageProps> = ({
  kind,
  title,
  idColumnLabel,
  addButtonLabel,
  addModalTitle,
  styles,
  renderExtraActions,
}) => {
  const { data, isLoading, isError, error, refetch } =
    useListMasterAccountsQuery({ kind });
  const [createMusicVendor, { isLoading: creatingMusic }] =
    useCreateMusicVendorMutation();
  const [createInstallerVendor, { isLoading: creatingInstaller }] =
    useCreateInstallerVendorMutation();
  const [startImpersonation, { isLoading: impersonating }] =
    useStartImpersonationMutation();
  const [reserveSupportTicket, { isLoading: reservingCaseId }] =
    useReserveSupportTicketMutation();

  const [filters, setFilters] = useState({
    companyName: undefined as string | undefined,
    city: undefined as string | undefined,
    state: undefined as string | undefined,
    status: undefined as string | undefined,
  });
  const [resetCounter, setResetCounter] = useState(0);
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState<CreateMasterAccountRequest>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [createResult, setCreateResult] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [impersonateTarget, setImpersonateTarget] =
    useState<VendorListRow | null>(null);
  const [impersonateReason, setImpersonateReason] = useState("");
  const [impersonateMinutes, setImpersonateMinutes] = useState(30);
  const [persistedSupportCaseId, setPersistedSupportCaseId] = useState<
    string | null
  >(null);
  const [caseIdReserved, setCaseIdReserved] = useState(false);

  const creating = creatingMusic || creatingInstaller;
  const vendors = useMemo(
    () => (data?.masterAccounts || []).map(toVendorRow),
    [data?.masterAccounts],
  );

  const filterOptions = useMemo(
    () => ({
      companyNames: vendors.map((v) => v.companyName),
      cities: [...new Set(vendors.map((v) => v.city).filter(Boolean))],
      states: [...new Set(vendors.map((v) => v.state).filter(Boolean))],
      status: ["Pending Approval", "Active", "Disabled"],
    }),
    [vendors],
  );

  const filteredVendors = vendors.filter((vendor) => {
    const statusFilter = filters.status
      ?.toLowerCase()
      .replace(/ /g, "_");
    return (
      (!filters.companyName || vendor.companyName === filters.companyName) &&
      (!filters.city || vendor.city === filters.city) &&
      (!filters.state || vendor.state === filters.state) &&
      (!statusFilter || vendor.status === statusFilter)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredVendors.length / 25));
  const pageRows = filteredVendors.slice((page - 1) * 25, page * 25);

  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setFilters({
      companyName: undefined,
      city: undefined,
      state: undefined,
      status: undefined,
    });
    setResetCounter((prev) => prev + 1);
    setPage(1);
  };

  const handleCreate = async () => {
    setFormError(null);
    setCreateResult(null);
    try {
      const body: CreateMasterAccountRequest = {
        businessName: form.businessName.trim(),
        address_street: form.address_street.trim(),
        address_city: form.address_city.trim(),
        address_state: form.address_state.trim(),
        address_zipCode: form.address_zipCode.trim(),
        address_country: form.address_country?.trim() || "US",
        billingPhone: form.billingPhone?.trim() || undefined,
        companyNumber: form.billingPhone?.trim() || undefined,
        billingEmail: form.billingEmail.trim() || form.ownerEmail.trim(),
        ownerFirstName: form.ownerFirstName.trim(),
        ownerLastName: form.ownerLastName.trim(),
        ownerEmail: form.ownerEmail.trim(),
        securityPin: form.securityPin?.trim() || undefined,
      };
      const result =
        kind === "music-vendor"
          ? await createMusicVendor(body).unwrap()
          : await createInstallerVendor(body).unwrap();
      setCreateResult(
        result.passwordSetupUrl
          ? `Vendor created. Password setup link: ${result.passwordSetupUrl}`
          : "Vendor created.",
      );
      setForm(emptyForm);
      setShowAddModal(false);
      refetch();
    } catch (err: any) {
      setFormError(
        err?.data?.message || err?.message || "Failed to create vendor",
      );
    }
  };

  const resetImpersonateForm = () => {
    setImpersonateTarget(null);
    setImpersonateReason("");
    setImpersonateMinutes(30);
    setPersistedSupportCaseId(null);
    setCaseIdReserved(false);
  };

  const openImpersonate = async (vendor: VendorListRow) => {
    setActionError(null);
    setImpersonateReason("");
    setImpersonateMinutes(30);
    setPersistedSupportCaseId(null);
    setCaseIdReserved(false);
    setImpersonateTarget(vendor);
    try {
      const result = await reserveSupportTicket({
        masterAccountId: vendor.masterAccountId,
      }).unwrap();
      const caseId = result.supportTicket?.ticketId;
      if (!caseId) {
        setActionError("Failed to reserve a Case ID");
        return;
      }
      setPersistedSupportCaseId(caseId);
      setCaseIdReserved(true);
    } catch (err: any) {
      setActionError(
        err?.data?.message || err?.message || "Failed to reserve Case ID",
      );
    }
  };

  const handleCreateSupportCaseAndStart = async () => {
    if (!impersonateTarget?.ownerAuth0Sub) {
      setActionError("This vendor has no owner membership to impersonate");
      return;
    }
    const caseId = persistedSupportCaseId?.trim();
    if (!caseId || !caseIdReserved) {
      setActionError("Case ID is not ready yet — wait for reservation");
      return;
    }
    if (impersonateReason.trim().length < 10) {
      setActionError("Reason is required (min 10 characters)");
      return;
    }
    setActionError(null);
    try {
      const result = await startImpersonation({
        masterAccountId: impersonateTarget.masterAccountId,
        userKey: impersonateTarget.ownerAuth0Sub,
        reason: impersonateReason,
        expectedDurationMinutes: impersonateMinutes,
        reservedSupportTicketId: caseId,
      }).unwrap();
      const payload = {
        token: result.token,
        ticketId: result.ticketId,
        expiresAt: result.expiresAt,
        correlationId: result.correlationId,
        masterAccountId: result.masterAccountId,
        userDisplayName: result.userDisplayName,
        userEmail: result.userEmail || null,
        saDisplayName: result.saDisplayName,
        businessName: result.businessName,
        impersonatedUserSub: result.impersonatedUserSub,
        supportTicketId: result.supportTicketId || caseId,
        supportModeSource: result.supportModeSource,
      };
      const encoded = encodeURIComponent(JSON.stringify(payload));
      window.open(
        `${getMaUiOrigin()}/support-mode?sm=${Date.now()}#kk_impersonation=${encoded}`,
        "_blank",
        "noopener,noreferrer",
      );
      resetImpersonateForm();
    } catch (err: any) {
      setActionError(
        err?.data?.message || err?.message || "Failed to create support case",
      );
    }
  };

  return (
    <Container fluid>
      <Card>
        <Card.Body>
          <Card.Title
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <PageTitle title={title} />
            <Button
              onClick={handleReset}
              style={{
                background: "white",
                color: "#333",
                border: "1px solid #e0e0e0",
                borderRadius: "6px",
                padding: "8px 16px",
                fontWeight: "500",
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>

          {createResult && (
            <Alert
              variant="success"
              dismissible
              onClose={() => setCreateResult(null)}
            >
              {createResult}
            </Alert>
          )}
          {actionError && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => setActionError(null)}
            >
              {actionError}
            </Alert>
          )}
          {isError && (
            <Alert variant="danger">
              {(error as any)?.data?.message ||
                (error as any)?.message ||
                "Failed to load vendors"}
            </Alert>
          )}

          <Form className={styles.form}>
            <Row>
              <Col>
                <Row className="mb-2">
                  <Col>
                    <Row>
                      <Col md={4}>
                        <FormItem
                          label="Company Name"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`companyName-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any company name"
                            value={filters.companyName}
                            onChange={(value) =>
                              handleFilterChange("companyName", value)
                            }
                            options={filterOptions.companyNames.map((name) => ({
                              label: name,
                              value: name,
                            }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="City"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`city-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any city"
                            value={filters.city}
                            onChange={(value) =>
                              handleFilterChange("city", value)
                            }
                            options={filterOptions.cities.map((city) => ({
                              label: city,
                              value: city,
                            }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4}>
                        <FormItem
                          label="State"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`state-reset-${resetCounter}`}
                            showSearch
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any state"
                            value={filters.state}
                            onChange={(value) =>
                              handleFilterChange("state", value)
                            }
                            options={filterOptions.states.map((state) => ({
                              label: state,
                              value: state,
                            }))}
                          />
                        </FormItem>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col>
                    <Row>
                      <Col md={4}>
                        <FormItem
                          label="Status"
                          className="customSelectWrapper"
                          layout="vertical"
                        >
                          <Select
                            key={`status-reset-${resetCounter}`}
                            allowClear
                            className={`customSelect ${styles.selectField}`}
                            placeholder="Any status"
                            value={filters.status}
                            onChange={(value) =>
                              handleFilterChange("status", value)
                            }
                            options={filterOptions.status.map((status) => ({
                              label: status,
                              value: status,
                            }))}
                          />
                        </FormItem>
                      </Col>
                      <Col md={4} className="d-flex align-items-center">
                        <div style={{ fontSize: 13, color: "#666" }}>
                          Tenant kind: <code>{kind}</code>
                        </div>
                      </Col>
                      <Col
                        md={4}
                        className="d-flex align-items-center mt-2 mt-md-0"
                      >
                        <Button
                          className="w-100"
                          style={{
                            background: "white",
                            color: "#e21e26",
                            border: "1px solid #e21e26",
                            borderRadius: "6px",
                            padding: "8px 16px",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            justifyContent: "center",
                            height: "50px",
                            marginTop: "8px",
                          }}
                          onClick={() => setShowAddModal(true)}
                        >
                          <PlusOutlined style={{ fontSize: 16 }} />
                          {addButtonLabel}
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>

          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>
                {title} ({filteredVendors.length})
              </h3>
            </div>

            {isLoading ? (
              <div className="py-5 text-center">
                <Spinner animation="border" />
              </div>
            ) : (
              <>
                <Table hover responsive className="legacy-row-height-table">
                  <thead>
                    <tr>
                      <th>{idColumnLabel}</th>
                      <th>Company Name</th>
                      <th>Owner</th>
                      <th>Company Address</th>
                      <th>Company Number</th>
                      <th>Status</th>
                      <th>Created On</th>
                      <th>Pin</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((vendor) => (
                      <tr key={vendor.masterAccountId}>
                        <td>
                          <code style={{ fontSize: 11 }}>
                            {vendor.vendorId}
                          </code>
                        </td>
                        <td>
                          <div>{vendor.companyName}</div>
                          <div style={{ fontSize: 11, color: "#888" }}>
                            <code>{kind}</code>
                          </div>
                        </td>
                        <td>
                          <div>{vendor.ownerDisplayName || "—"}</div>
                          <div style={{ fontSize: 12, color: "#666" }}>
                            {vendor.ownerEmail || "—"}
                          </div>
                        </td>
                        <td>{vendor.companyAddress}</td>
                        <td>{vendor.companyNumber}</td>
                        <td>
                          <span
                            className={`badge ${
                              vendor.status === "active"
                                ? "bg-success"
                                : vendor.status === "pending_approval"
                                  ? "bg-warning"
                                  : "bg-secondary"
                            }`}
                          >
                            {formatStatusLabel(vendor.status)}
                          </span>
                        </td>
                        <td>{vendor.createdOn}</td>
                        <td>{vendor.pinLabel}</td>
                        <td>
                          <div className={styles.actionButtons}>
                            <Button
                              variant="link"
                              size="sm"
                              className={styles.actionLink}
                              disabled={
                                !vendor.ownerAuth0Sub || impersonating
                              }
                              onClick={() => void openImpersonate(vendor)}
                              title="Support Mode / Impersonate"
                            >
                              🛟
                            </Button>
                            {renderExtraActions?.(vendor)}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {pageRows.length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-center">
                          No {title.toLowerCase()} found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                {totalPages > 1 && (
                  <PaginationRow
                    page={page}
                    onPageChange={(p) => setPage(p)}
                    totalPages={typeof totalPages === "number" ? totalPages : undefined}
                    onClickPrevious={() => setPage((p) => Math.max(1, p - 1))}
                    handleOnClickNext={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                    disableLeft={page === 1}
                    disableNext={page === totalPages}
                  />
                )}
              </>
            )}
          </div>
        </Card.Body>
      </Card>

      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{addModalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <Form>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Vendor / company name *</Form.Label>
                  <Form.Control
                    value={form.businessName}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, businessName: e.target.value }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Owner first name *</Form.Label>
                  <Form.Control
                    value={form.ownerFirstName}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        ownerFirstName: e.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Owner last name *</Form.Label>
                  <Form.Control
                    value={form.ownerLastName}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, ownerLastName: e.target.value }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Owner email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={form.ownerEmail}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, ownerEmail: e.target.value }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company number / phone</Form.Label>
                  <Form.Control
                    value={form.billingPhone || ""}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, billingPhone: e.target.value }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Street address *</Form.Label>
                  <Form.Control
                    value={form.address_street}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        address_street: e.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>City *</Form.Label>
                  <Form.Control
                    value={form.address_city}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, address_city: e.target.value }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>State *</Form.Label>
                  <Form.Control
                    value={form.address_state}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, address_state: e.target.value }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>ZIP *</Form.Label>
                  <Form.Control
                    value={form.address_zipCode}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        address_zipCode: e.target.value,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Security PIN (4 digits)</Form.Label>
                  <Form.Control
                    inputMode="numeric"
                    maxLength={4}
                    value={form.securityPin || ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        securityPin: e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 4),
                      }))
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
            <Alert variant="light" className="mb-0">
              Creates an isolated Master Account with frozen{" "}
              <code>kind={kind}</code> via canonical provisioning.
            </Alert>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={
              creating ||
              !form.businessName.trim() ||
              !form.ownerEmail.trim() ||
              !form.ownerFirstName.trim() ||
              !form.ownerLastName.trim()
            }
          >
            {creating ? "Creating…" : addButtonLabel}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!impersonateTarget}
        onHide={resetImpersonateForm}
      >
        <Modal.Header closeButton>
          <Modal.Title>Support Mode</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Enter <strong>{impersonateTarget?.companyName}</strong> as{" "}
            {impersonateTarget?.ownerDisplayName ||
              impersonateTarget?.ownerEmail}
            .
          </p>
          <div className="mb-3" data-testid="persisted-support-case">
            {reservingCaseId && !persistedSupportCaseId ? (
              <div className="small text-muted">Reserving Case ID…</div>
            ) : persistedSupportCaseId && caseIdReserved ? (
              <>
                <div className="d-flex align-items-baseline gap-2">
                  <span className="text-uppercase small text-muted">Case ID</span>
                  <span
                    className="fw-semibold font-monospace user-select-all"
                    style={{ fontSize: "1rem" }}
                  >
                    {persistedSupportCaseId}
                  </span>
                </div>
                <div className="small text-muted mt-1">
                  Reserved by KrossKast. Becomes active when you click Create
                  Support Case.
                </div>
              </>
            ) : (
              <div className="small text-muted">
                Unable to reserve a Case ID. Close and try again.
              </div>
            )}
          </div>
          <Form.Group className="mb-3">
            <Form.Label>What does the client need help with?</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={impersonateReason}
              onChange={(e) => setImpersonateReason(e.target.value)}
              placeholder="What does the client need help with?"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Expected duration (minutes, max 240)</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={240}
              value={impersonateMinutes}
              onChange={(e) =>
                setImpersonateMinutes(Number(e.target.value) || 30)
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetImpersonateForm}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleCreateSupportCaseAndStart}
            disabled={
              impersonating ||
              reservingCaseId ||
              impersonateReason.trim().length < 10 ||
              !impersonateTarget?.ownerAuth0Sub ||
              !persistedSupportCaseId ||
              !caseIdReserved
            }
          >
            {impersonating ? "Starting…" : "Create Support Case"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default VendorAccountsPage;
